import { fetchNetworkStats, type NetworkStats, type StatsPeriod } from '@/api/statsApi';
import { useClient } from '@/clients/useClient';
import { useStatsFilters } from '@/stats/StatsFiltersContext';
import { useCachedStats } from './useCachedStats';

/** Exported for test isolation only — do not call in production code. */
export function _clearStatsCache() {
    statsCache.clear();
}

// Module-level cache keyed by `clientId:period:filtersKey`, kept across component remounts
// within the browser session so that navigating away from and back to Find & Fix does not
// flash a loading skeleton when the same data was already fetched. An entry is dropped on
// fetch error so stale data is never served after a failure.
const statsCache = new Map<string, NetworkStats>();

/**
 * The selected client's network statistics for the Find & Fix header over the chosen
 * period, under the committed map filters (bridged up from MapView — the bar must show the
 * filtered network, not disagree with the map beside it).
 */
export function useNetworkStats(period: StatsPeriod): {
    data: NetworkStats | null;
    isLoading: boolean;
    isError: boolean;
} {
    const { selectedClient } = useClient();
    const clientId = selectedClient?.id ?? null;
    const { filters, filtersKey } = useStatsFilters();

    // The key encodes every dimension so any of them changing triggers a re-fetch.
    return useCachedStats(
        clientId
            ? {
                  key: `${clientId}:${period}:${filtersKey}`,
                  fetch: (signal) => fetchNetworkStats(clientId, period, filters, signal),
              }
            : null,
        statsCache,
    );
}
